import { Controller } from '../utils/controller';

export default class extends Controller {
  static targets = ['countDownText', 'offer', 'expired'];
  static requiredTargets = [];

  static values = {
    endDate: {
      type: String,
      default: ''
    },
    timerDoneText: {
      type: String,
      default: ''
    },
    daysLabel: {
      type: String,
      default: ''
    },
    hoursLabel: {
      type: String,
      default: ''
    },
    minutesLabel: {
      type: String,
      default: ''
    },
    secondsLabel: {
      type: String,
      default: ''
    },
    boxesBg: {
      type: String,
      default: ''
    },
    boxesBorder: {
      type: String,
      default: ''
    },
    hideSeconds: {
      type: Boolean,
      default: false
    }
  };

  static classes = ['hidden', 'showExpired'];

  // TARGETS
  declare countDownTextTarget: HTMLElement;
  declare offerTarget: HTMLElement;
  declare expiredTarget: HTMLElement;

  // VALUES
  declare daysLabelValue: string;
  declare hoursLabelValue: string;
  declare minutesLabelValue: string;
  declare secondsLabelValue: string;
  declare timerDoneTextValue: string;
  declare boxesBgValue: string;
  declare boxesBorderValue: string;
  declare hideSecondsValue: boolean;

  // CLASSES
  declare hiddenClasses: string[];
  declare showExpiredClasses: string[];

  // LOCAL STATE
  private intervalId?: ReturnType<typeof setInterval> | null;
  private targetDate: number;
  private timeLeft: number;
  private endDateValue: string;

  connect(): void {
    // force initial computation
    this.updateText();

    this.intervalId = setInterval(this.updateText.bind(this), 1000);
  }
  disconnect(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.intervalId = null;
  }

  updateText(): void {
    this.targetDate = new Date(this.endDateValue).getTime();
    const now = new Date().getTime();
    this.timeLeft = this.targetDate - now;

    if (this.timeLeft < 0) {
      this.offerTarget.classList.add(...this.hiddenClasses);

      if (this.timerDoneTextValue) {
        this.expiredTarget.classList.add(...this.showExpiredClasses);
        this.expiredTarget.classList.remove(...this.hiddenClasses);
      }

      this.timeLeft = 0;
    }

    const days = Math.floor(this.timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((this.timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((this.timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((this.timeLeft % (1000 * 60)) / 1000);

    this.countDownTextTarget.innerHTML = `
      ${this.renderPart({
        content: this.showTwoDigitNumber(days),
        attr: 'data-days',
        renderValue: true,
        separator: ':',
        label: this.daysLabelValue,
        labelAttr: 'data-days-label'
      })}
      ${this.renderPart({
        content: this.showTwoDigitNumber(hours),
        attr: 'data-hours',
        renderValue: true,
        separator: ':',
        label: this.hoursLabelValue,
        labelAttr: 'data-hours-label'
      })}
      ${this.renderPart({
        content: this.showTwoDigitNumber(minutes),
        attr: 'data-minutes',
        renderValue: true,
        separator: ':',
        label: this.minutesLabelValue,
        labelAttr: 'data-minutes-label'
      })}
      ${
        (!this.hideSecondsValue &&
          this.renderPart({
            content: this.showTwoDigitNumber(seconds),
            attr: 'data-seconds',
            renderValue: true,
            separator: '',
            label: this.secondsLabelValue,
            labelAttr: 'data-seconds-label'
          })) ||
        ''
      }
    `;
  }

  private showTwoDigitNumber(value) {
    return value < 10 ? ('0' + value).slice(-2) : value;
  }

  private countdownFinishedClass(value) {
    return value == '00' ? 'cd-finished' : '';
  }

  private generateDynamicStyles() {
    let styles = '';

    styles += this.boxesBgValue ? `background-color: #${this.boxesBgValue}; ` : '';
    styles += this.boxesBorderValue ? `border-color: #${this.boxesBorderValue}; ` : '';

    return styles;
  }

  private renderPart({
    renderValue,
    content,
    separator,
    label,
    attr,
    labelAttr
  }: {
    renderValue: boolean;
    content: unknown;
    separator?: unknown;
    attr?: string;
    label?: string;
    labelAttr?: string;
  }) {
    return renderValue
      ? `<span ${attr} class="${this.countdownFinishedClass(
          content
        )}" style="${this.generateDynamicStyles()}">
        <span class="cd-header">${content}</span>
        ${(label && `<span class="cd-label" ${labelAttr}>${label}</span>`) || ''}
      </span>
      ${separator ? `<span class="cd-separator" data-separator>${separator}</span>` : ''}
      `
      : '';
  }
}
